import { BufferManager } from '../memory/BufferManager';

/** @import { MemoryBlock } from '../memory/MemoryBlock' */

const DEFAULT_ALIGNMENT = 1;

/**
 * Thin wrapper around BufferManager that uses WebGPU buffers.
 */
export class BufferManagerWebGPU {

  /** @type {GPUDevice} */
  #device;

  /** @type {BufferManager} */
  #bufferManager;

  /**
   * @param {GPUDevice} device - WebGPU device.
   * @param {GPUBufferDescriptor} bufferDesc - Buffer descriptor.
   * @param {boolean} [freesAreBatched=false] - Whether to batch frees.
   */
  constructor(device, bufferDesc, options) {
    this.#device = device;

    // Make a copy of the buffer descriptor to avoid modifying the original object
    bufferDesc = { ...bufferDesc };

    const createBufferCallback = (size) => {
      bufferDesc.size = size;
      return this.#device.createBuffer(bufferDesc);
    };

    const destroyBufferCallback = (buffer) => {
      buffer.destroy();
    };

    this.#bufferManager = new BufferManager(bufferDesc.size, createBufferCallback, destroyBufferCallback, options);
    this.#bufferManager.label = bufferDesc.label ?? undefined;
  }

  /**
   * Destroys all buffers and clears the free lists.
   * This should be called when the BufferManager is no longer needed.
   */
  dtor() {
    this.#bufferManager.dtor();
  }

  /**
   * Allocates a block of memory with the specified size and alignment.
   * Creates a new buffer if necessary.
   *
   * @param {number} size - Size of the memory block to allocate in bytes
   * @param {number} [alignment=DEFAULT_ALIGNMENT] - Alignment requirement in bytes
   * @returns {MemoryBlock} Allocated memory block
   */
  allocate(size, alignment = DEFAULT_ALIGNMENT) {
    return this.#bufferManager.allocate(size, alignment);
  }

  /**
   * Either frees the block immediately or adds it to the batched list.
   *
   * @param {MemoryBlock} block - The memory block to free
   */
  free(block) {
    this.#bufferManager.free(block);
  }
  /**
   * Flushes all batched free blocks.
   */
  flushFreeBlocks() {
    this.#bufferManager.flushFreeBlocks();
  }

  /**
   * Gets the total size of all allocated buffers.
   *
   * @returns {number} Total size in bytes
   */
  getTotalSize() {
    return this.#bufferManager.getTotalSize();
  }

  /**
   * Gets the size of a single buffer entry.
   *
   * @returns {number} Buffer size in bytes
   */
  getDefaultBufferSize() {
    return this.#bufferManager.getDefaultBufferSize();
  }

  /**
   * Gets the total amount of free space across all buffers.
   *
   * @returns {number} Total free space in bytes
   */
  getTotalFreeSpace() {
    return this.#bufferManager.getTotalFreeSpace();
  }

  /**
   * Gets the total amount of used space across all buffers.
   *
   * @returns {number} Total used space in bytes
   */
  getUsedBytes() {
    return this.#bufferManager.getUsedBytes();
  }
}